import { useEffect } from "react";

// ── Types ──────────────────────────────────────────────────────────────────────

interface OrganizationSchemaProps {
  type?: "Organization" | "Corporation";
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface ProductSchemaProps {
  name: string;
  description?: string;
  image?: string;
  brand?: string;
  sku?: string;
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
    seller?: string;
  };
  aggregateRating?: {
    ratingValue?: number;
    reviewCount?: number;
  };
}

interface ArticleSchemaProps {
  headline: string;
  description?: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  author?: string;
  publisher?: string;
  url?: string;
  keywords?: string[];
}

interface EventSchemaProps {
  name: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  image?: string;
  url?: string;
  organizer?: string;
  status?: "EventScheduled" | "EventCancelled" | "EventPostponed";
}

interface FAQItem {
  question: string;
  answer: string;
}

interface LocalBusinessSchemaProps {
  name: string;
  description?: string;
  url?: string;
  telephone?: string;
  email?: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  };
  image?: string;
  logo?: string;
  sameAs?: string[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

const SITE_URL = typeof window !== "undefined" ? `${window.location.protocol}//${window.location.host}` : "https://kcania.bizwell.com.vn";

const KCANIA_ORG = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  "name": "KCANIA – Korea Carbon and Nano Industry Association",
  "alternateName": "Hiệp Hội Công Nghiệp Carbon và Nano Hàn Quốc",
  "url": SITE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": "https://static.readdy.ai/image/302c4174898c881693fe2d12476be64a/fcb736df9a5a33bf7d4c294e6dc25119.png",
    "width": 400,
    "height": 200,
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+84-965-180-170",
      "contactType": "customer service",
      "availableLanguage": ["Vietnamese", "Korean"],
    },
    {
      "@type": "ContactPoint",
      "telephone": "+84-28-6288-4688",
      "contactType": "customer service",
      "availableLanguage": ["Vietnamese", "Korean"],
    },
  ],
  "email": "bizwell@bizwell.com.vn",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "LE.0354, LE.0355, Chung cư Lexington, 67 Mai Chí Thọ",
    "addressLocality": "Thành phố Hồ Chí Minh",
    "addressCountry": "VN",
  },
  "sameAs": [
    "https://www.facebook.com/profile.php?id=61572163886566",
    "https://www.kcania.or.kr/html/",
    "https://bizwell.com.vn",
  ],
  "description": "Nền tảng B2B kết nối doanh nghiệp công nghệ Carbon Nano Hàn Quốc với thị trường Việt Nam. Cung cấp thông tin sản phẩm, hợp tác kỹ thuật, sự kiện và tư vấn song ngữ Hàn–Việt.",
  "knowsAbout": [
    "Carbon Nano Technology",
    "Korean Manufacturing",
    "B2B Vietnam Korea Trade",
    "Industrial Technology Korea",
    "Chemical Industry Korea",
  ],
  "areaServed": ["VN", "KR"],
  "availableLanguage": ["vi", "ko"],
};

const injectSchema = (id: string, schema: Record<string, unknown>) => {
  const existing = document.querySelector(`script[data-schema="${id}"]`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute("data-schema", id);
  script.textContent = JSON.stringify(schema, null, 0);
  document.head.appendChild(script);
};

const removeSchema = (id: string) => {
  const el = document.querySelector(`script[data-schema="${id}"]`);
  if (el) el.remove();
};

// ── Organization / Website (global) ───────────────────────────────────────────

export const OrganizationSchema = ({ type = "Organization" }: OrganizationSchemaProps) => {
  useEffect(() => {
    const org = {
      "@context": "https://schema.org",
      "@graph": [
        { ...KCANIA_ORG, "@type": type },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          "url": SITE_URL,
          "name": "KCANIA – Kết Nối Doanh Nghiệp Công Nghệ Hàn–Việt",
          "description": "Khám phá sản phẩm công nghệ tiên tiến từ các doanh nghiệp Hàn Quốc, tìm kiếm đối tác hợp tác kỹ thuật và cập nhật tin tức thị trường Hàn–Việt.",
          "inLanguage": "vi",
          "publisher": { "@id": `${SITE_URL}/#organization` },
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": `${SITE_URL}/danh-muc?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        },
      ],
    };
    injectSchema("org-website", org);
    return () => removeSchema("org-website");
  }, [type]);
  return null;
};

// ── Breadcrumb ─────────────────────────────────────────────────────────────────

export const BreadcrumbSchema = ({ items }: { items: BreadcrumbItem[] }) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": item.name,
        "item": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
      })),
    };
    injectSchema("breadcrumb", schema);
    return () => removeSchema("breadcrumb");
  }, [items]);
  return null;
};

// ── Product ────────────────────────────────────────────────────────────────────

export const ProductSchema = ({
  name,
  description,
  image,
  brand,
  sku,
  offers,
  aggregateRating,
}: ProductSchemaProps) => {
  useEffect(() => {
    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": name,
      ...(description && { "description": description }),
      ...(image && { "image": image }),
      ...(brand && {
        "brand": { "@type": "Brand", "name": brand },
        "manufacturer": {
          "@type": "Organization",
          "name": brand,
          "addressCountry": "KR",
        },
      }),
      ...(sku && { "sku": sku }),
      "inProductGroupWithID": sku,
      "countryOfOrigin": { "@type": "Country", "name": "South Korea" },
    };

    if (offers) {
      schema["offers"] = {
        "@type": "Offer",
        "priceCurrency": offers.priceCurrency || "VND",
        "price": offers.price || "0",
        "availability": offers.availability || "https://schema.org/InStock",
        "url": window.location.href,
        ...(offers.seller && {
          "seller": {
            "@type": "Organization",
            "name": offers.seller,
            "@id": `${SITE_URL}/#organization`,
          },
        }),
      };
    }

    if (aggregateRating) {
      schema["aggregateRating"] = {
        "@type": "AggregateRating",
        "ratingValue": aggregateRating.ratingValue || 4.7,
        "reviewCount": aggregateRating.reviewCount || 1,
        "bestRating": 5,
      };
    }

    injectSchema("product", schema);
    return () => removeSchema("product");
  }, [name, description, image, brand, sku, offers, aggregateRating]);
  return null;
};

// ── Article ────────────────────────────────────────────────────────────────────

export const ArticleSchema = ({
  headline,
  description,
  image,
  datePublished,
  dateModified,
  author,
  publisher,
  url,
  keywords,
}: ArticleSchemaProps) => {
  useEffect(() => {
    const articleUrl = url || window.location.href;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": headline,
      ...(description && { "description": description }),
      ...(image && { "image": { "@type": "ImageObject", "url": image } }),
      ...(datePublished && { "datePublished": datePublished }),
      "dateModified": dateModified || datePublished || new Date().toISOString(),
      "author": {
        "@type": "Person",
        "name": author || "KCANIA Editorial Team",
      },
      "publisher": {
        "@type": "Organization",
        "name": publisher || "KCANIA – Korea Carbon Nano Industry Association",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/302c4174898c881693fe2d12476be64a/fcb736df9a5a33bf7d4c294e6dc25119.png",
        },
      },
      "url": articleUrl,
      "mainEntityOfPage": { "@type": "WebPage", "@id": articleUrl },
      "inLanguage": "vi",
      "isPartOf": { "@type": "WebSite", "@id": `${SITE_URL}/#website` },
      ...(keywords && { "keywords": keywords.join(", ") }),
      "about": {
        "@type": "Thing",
        "name": "Quan hệ thương mại Hàn Quốc – Việt Nam",
        "sameAs": "https://en.wikipedia.org/wiki/South_Korea%E2%80%93Vietnam_relations",
      },
    };
    injectSchema("article", schema);
    return () => removeSchema("article");
  }, [headline, description, image, datePublished, dateModified, author, publisher, url, keywords]);
  return null;
};

// ── Event ──────────────────────────────────────────────────────────────────────

export const EventSchema = ({
  name,
  description,
  startDate,
  endDate,
  location,
  image,
  url,
  organizer,
  status = "EventScheduled",
}: EventSchemaProps) => {
  useEffect(() => {
    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Event",
      "name": name,
      ...(description && { "description": description }),
      "eventStatus": `https://schema.org/${status}`,
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      ...(startDate && { "startDate": startDate }),
      ...(endDate && { "endDate": endDate }),
      ...(location && {
        "location": {
          "@type": "Place",
          "name": location,
          "address": { "@type": "PostalAddress", "addressLocality": location, "addressCountry": "VN" },
        },
      }),
      ...(image && { "image": image }),
      "url": url || window.location.href,
      "organizer": {
        "@type": "Organization",
        "name": organizer || "KCANIA – Korea Carbon Nano Industry Association",
        "url": SITE_URL,
      },
      "inLanguage": "vi",
    };
    injectSchema("event", schema);
    return () => removeSchema("event");
  }, [name, description, startDate, endDate, location, image, url, organizer, status]);
  return null;
};

// ── FAQ ────────────────────────────────────────────────────────────────────────

export const FAQSchema = ({ faqs }: { faqs: FAQItem[] }) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    };
    injectSchema("faq", schema);
    return () => removeSchema("faq");
  }, [faqs]);
  return null;
};

// ── Local Business ─────────────────────────────────────────────────────────────

export const LocalBusinessSchema = ({
  name,
  description,
  url,
  telephone,
  email,
  address,
  image,
  logo,
  sameAs,
}: LocalBusinessSchemaProps) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": name,
      ...(description && { "description": description }),
      "url": url || SITE_URL,
      ...(telephone && { "telephone": telephone }),
      ...(email && { "email": email }),
      ...(address && {
        "address": {
          "@type": "PostalAddress",
          ...address,
        },
      }),
      ...(image && { "image": image }),
      ...(logo && { "logo": logo }),
      ...(sameAs && { "sameAs": sameAs }),
      "priceRange": "Liên hệ",
      "currenciesAccepted": "VND",
      "areaServed": "VN",
      "availableLanguage": ["Vietnamese", "Korean"],
    };
    injectSchema("local-business", schema);
    return () => removeSchema("local-business");
  }, [name, description, url, telephone, email, address, image, logo, sameAs]);
  return null;
};

// ── ItemList (for category/list pages) ────────────────────────────────────────

interface ItemListSchemaProps {
  name: string;
  description?: string;
  url?: string;
  items: Array<{ name: string; url: string; image?: string; description?: string }>;
}

export const ItemListSchema = ({ name, description, url, items }: ItemListSchemaProps) => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": name,
      ...(description && { "description": description }),
      "url": url || window.location.href,
      "numberOfItems": items.length,
      "itemListElement": items.slice(0, 20).map((item, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": item.name,
        "url": item.url.startsWith("http") ? item.url : `${SITE_URL}${item.url}`,
        ...(item.image && { "image": item.image }),
        ...(item.description && { "description": item.description }),
      })),
    };
    injectSchema("itemlist", schema);
    return () => removeSchema("itemlist");
  }, [name, description, url, items]);
  return null;
};